import { FileText, Image as ImageIcon, Mic, File as FileIcon, X } from "lucide-react";

const KIND_LABEL = {
  image: "image",
  audio: "audio",
  text: "doc",
  binary: "file",
};

function iconFor(kind) {
  if (kind === "image") return ImageIcon;
  if (kind === "audio") return Mic;
  if (kind === "text") return FileText;
  return FileIcon;
}

function prettySize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Small chip showing a pending or attached file.
 * Variants:
 *   - "pending" — sits above the input while user is composing; has × remove button.
 *   - "compact" — sits inside a sent user bubble in the chat thread.
 */
export default function AttachmentChip({ attachment, variant = "compact", onRemove, testId }) {
  const Icon = iconFor(attachment.kind);
  const isImage = attachment.kind === "image" && attachment.data_url;
  const isPending = variant === "pending";

  if (isImage && !isPending) {
    return (
      <div className="inline-block mr-2 mb-2 align-top" data-testid={testId}>
        <img
          src={attachment.data_url}
          alt={attachment.filename}
          className="max-h-48 max-w-[220px] rounded-lg border border-[var(--border-soft)] object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={
        isPending
          ? "inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[var(--bg-pill)] border border-[var(--border-soft)] text-[12px] font-mono-ui text-[var(--text-muted)] max-w-[260px]"
          : "inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-[var(--bg)] border border-[var(--border-soft)] text-[11px] font-mono-ui text-[var(--text-muted)] mr-2 mb-2 max-w-[240px]"
      }
      data-testid={testId}
    >
      {isImage && isPending ? (
        <img
          src={attachment.data_url}
          alt=""
          className="w-6 h-6 rounded object-cover shrink-0"
        />
      ) : (
        <Icon size={12} className="shrink-0" />
      )}
      <span className="truncate" title={attachment.filename}>
        {attachment.filename}
      </span>
      <span className="text-[var(--text-dim)] shrink-0 tracking-wider uppercase text-[10px]">
        {KIND_LABEL[attachment.kind] || "file"}
        {attachment.size ? ` · ${prettySize(attachment.size)}` : ""}
      </span>
      {isPending && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 shrink-0 text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors"
          data-testid={`${testId}-remove`}
          aria-label={`Remove ${attachment.filename}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}
